const WORKGROUP_SIZE = 256;

// Refit keeps the Morton topology and parent links from the last rebuild.
// Only leaf bounds and the transient bottom-up visit counters are refreshed.
export const lbvhResetRefitStateShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read_write> visitCount: array<atomic<u32>>;

@compute @workgroup_size(${WORKGROUP_SIZE})
fn resetRefitState(@builtin(global_invocation_id) globalId: vec3u) {
  let idx = globalId.x;
  if (idx >= uniforms.primCount * 2u) { return; }
  atomicStore(&visitCount[idx], 0u);
}
`;

export const lbvhRefitLeavesShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _workgroupCount: u32,
  positionStride: u32,
  _pad0: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read> positions: array<f32>;
@group(0) @binding(2) var<storage, read> indices: array<u32>;
@group(0) @binding(3) var<storage, read_write> bvh2Nodes: array<BVH2Node>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

fn loadPosition(vertexIdx: u32) -> vec3f {
  let base = vertexIdx * uniforms.positionStride;
  return vec3f(positions[base], positions[base + 1u], positions[base + 2u]);
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn refitLeaves(@builtin(global_invocation_id) globalId: vec3u) {
  let primIdx = globalId.x;
  if (primIdx >= uniforms.primCount) { return; }
  let base = primIdx * 3u;
  let v0 = loadPosition(indices[base]);
  let v1 = loadPosition(indices[base + 1u]);
  let v2 = loadPosition(indices[base + 2u]);
  var node = bvh2Nodes[primIdx];
  node.boundsMin = min(min(v0, v1), v2);
  node.boundsMax = max(max(v0, v1), v2);
  node.leftChild = INVALID_IDX;
  node.rightChild = primIdx;
  bvh2Nodes[primIdx] = node;
}
`;

export const lbvhInitStateShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read_write> parentIdx: array<u32>;
@group(0) @binding(2) var<storage, read_write> visitCount: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

@compute @workgroup_size(${WORKGROUP_SIZE})
fn initState(@builtin(global_invocation_id) globalId: vec3u) {
  let idx = globalId.x;
  let maxNodes = uniforms.primCount * 2u;
  if (idx >= maxNodes) {
    return;
  }

  parentIdx[idx] = INVALID_IDX;
  atomicStore(&visitCount[idx], 0u);
}
`;

export const lbvhBuildTopologyShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read> mortonCodes: array<u32>;
@group(0) @binding(2) var<storage, read> clusterIdx: array<u32>;
@group(0) @binding(3) var<storage, read_write> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(4) var<storage, read_write> parentIdx: array<u32>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

fn delta(idx: i32, other: i32, n: i32) -> i32 {
  if (other < 0 || other >= n) {
    return -1;
  }

  let a = u32(idx);
  let b = u32(other);
  let ka = mortonCodes[a];
  let kb = mortonCodes[b];

  if (ka == kb) {
    return 32 + i32(countLeadingZeros(a ^ b));
  }

  return i32(countLeadingZeros(ka ^ kb));
}

fn determineRange(idx: i32, n: i32) -> vec2i {
  if (idx == 0) {
    return vec2i(0, n - 1);
  }

  let deltaLeft = delta(idx, idx - 1, n);
  let deltaRight = delta(idx, idx + 1, n);
  let direction = select(-1, 1, deltaRight > deltaLeft);

  let deltaMin = delta(idx, idx - direction, n);
  var lMax = 2;
  loop {
    let nextIdx = idx + lMax * direction;
    if (delta(idx, nextIdx, n) <= deltaMin) {
      break;
    }
    lMax = lMax * 2;
  }

  var length = 0;
  var t = lMax / 2;
  loop {
    if (t <= 0) {
      break;
    }

    let nextLength = length + t;
    let nextIdx = idx + nextLength * direction;
    if (delta(idx, nextIdx, n) > deltaMin) {
      length = nextLength;
    }

    t = t / 2;
  }

  let j = idx + length * direction;
  if (direction < 0) {
    return vec2i(j, idx);
  }

  return vec2i(idx, j);
}

fn findSplit(first: i32, last: i32, n: i32) -> i32 {
  let commonPrefix = delta(first, last, n);
  var split = first;
  var step = last - first;

  loop {
    step = (step + 1) / 2;
    if (step <= 0) {
      break;
    }

    let candidate = split + step;
    if (candidate < last && delta(first, candidate, n) > commonPrefix) {
      split = candidate;
    }

    if (step == 1) {
      break;
    }
  }

  return split;
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn buildTopology(@builtin(global_invocation_id) globalId: vec3u) {
  let internalId = globalId.x;
  if (uniforms.primCount <= 1u || internalId >= uniforms.primCount - 1u) {
    return;
  }

  let n = i32(uniforms.primCount);
  let idx = i32(internalId);
  let range = determineRange(idx, n);
  let split = findSplit(range.x, range.y, n);

  let nodeIdx = uniforms.primCount + internalId;

  var leftChild = INVALID_IDX;
  if (split == range.x) {
    leftChild = clusterIdx[u32(split)];
  } else {
    leftChild = uniforms.primCount + u32(split);
  }

  var rightChild = INVALID_IDX;
  if (split + 1 == range.y) {
    rightChild = clusterIdx[u32(split + 1)];
  } else {
    rightChild = uniforms.primCount + u32(split + 1);
  }

  var node = bvh2Nodes[nodeIdx];
  node.leftChild = leftChild;
  node.rightChild = rightChild;
  bvh2Nodes[nodeIdx] = node;

  parentIdx[leftChild] = nodeIdx;
  parentIdx[rightChild] = nodeIdx;
}
`;

export const lbvhSeedInternalShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(2) var<storage, read_write> visitCount: array<atomic<u32>>;
@group(0) @binding(3) var<storage, read_write> activeListOut: array<u32>;
@group(0) @binding(4) var<storage, read_write> activeCountOut: atomic<u32>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

@compute @workgroup_size(${WORKGROUP_SIZE})
fn seedInternal(@builtin(global_invocation_id) globalId: vec3u) {
  let sortedLeafIdx = globalId.x;
  if (sortedLeafIdx >= uniforms.primCount) {
    return;
  }

  // Every physical leaf must arrive exactly once.  Sorting is relevant to
  // topology construction only; clusterIdx[0] is later overwritten with the
  // root handle, so it cannot be used as a persistent leaf permutation.
  let leafNodeIdx = sortedLeafIdx;
  let parent = parentIdx[leafNodeIdx];
  if (parent == INVALID_IDX) {
    return;
  }

  // First child arrival stores 0 -> 1, second stores 1 -> 2.
  // Only second arrival can enqueue this internal node as ready.
  let previous = atomicAdd(&visitCount[parent], 1u);
  if (previous == 1u) {
    let writeIdx = atomicAdd(&activeCountOut, 1u);
    activeListOut[writeIdx] = parent;
  }
}
`;

export const lbvhRefitWaveShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read_write> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(2) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(3) var<storage, read_write> visitCount: array<atomic<u32>>;
@group(0) @binding(4) var<storage, read> activeListIn: array<u32>;
@group(0) @binding(5) var<storage, read_write> activeListOut: array<u32>;
@group(0) @binding(6) var<storage, read_write> activeCountIn: atomic<u32>;
@group(0) @binding(7) var<storage, read_write> activeCountOut: atomic<u32>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

@compute @workgroup_size(${WORKGROUP_SIZE})
fn refitWave(@builtin(global_invocation_id) globalId: vec3u) {
  // Keep the uniform binding live in auto-layout.
  if (uniforms.primCount == 0u) {
    return;
  }

  let idx = globalId.x;
  let activeCount = atomicLoad(&activeCountIn);
  if (idx >= activeCount) {
    return;
  }

  let nodeIdx = activeListIn[idx];
  if (nodeIdx == INVALID_IDX) {
    return;
  }

  let node = bvh2Nodes[nodeIdx];
  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (c0 == INVALID_IDX || c1 == INVALID_IDX) {
    return;
  }

  let mergedMin = min(bvh2Nodes[c0].boundsMin, bvh2Nodes[c1].boundsMin);
  let mergedMax = max(bvh2Nodes[c0].boundsMax, bvh2Nodes[c1].boundsMax);
  bvh2Nodes[nodeIdx].boundsMin = mergedMin;
  bvh2Nodes[nodeIdx].boundsMax = mergedMax;

  let parent = parentIdx[nodeIdx];
  if (parent == INVALID_IDX) {
    return;
  }

  let previous = atomicAdd(&visitCount[parent], 1u);
  if (previous == 1u) {
    let writeIdx = atomicAdd(&activeCountOut, 1u);
    activeListOut[writeIdx] = parent;
  }
}
`;

export const lbvhUpdateDispatchShader = /* wgsl */`
@group(0) @binding(0) var<storage, read_write> activeCountIn: atomic<u32>;
@group(0) @binding(1) var<storage, read_write> indirectDispatch: array<u32>;
@group(0) @binding(2) var<storage, read_write> activeCountOut: atomic<u32>;
@group(0) @binding(3) var<storage, read_write> refitDepth: atomic<u32>;

const WORKGROUP_SIZE: u32 = ${WORKGROUP_SIZE}u;

@compute @workgroup_size(1)
fn updateDispatch() {
  let count = atomicLoad(&activeCountIn);
  if (count > 0u) {
    // One invocation runs before every refit wave.  Counting non-empty waves
    // gives the exact depth needed by this topology without a second tree walk.
    atomicAdd(&refitDepth, 1u);
  }
  let workgroups = (count + WORKGROUP_SIZE - 1u) / WORKGROUP_SIZE;
  indirectDispatch[0] = workgroups;
  indirectDispatch[1] = 1u;
  indirectDispatch[2] = 1u;
  atomicStore(&activeCountOut, 0u);
}
`;

export const lbvhFinalizeShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read_write> clusterIdx: array<u32>;
@group(0) @binding(2) var<storage, read_write> nodeCounter: atomic<u32>;

@compute @workgroup_size(1)
fn finalizeTree() {
  if (uniforms.primCount == 0u) {
    atomicStore(&nodeCounter, 0u);
    return;
  }

  let rootIdx = select(0u, uniforms.primCount, uniforms.primCount > 1u);
  clusterIdx[0] = rootIdx;
  atomicStore(&nodeCounter, uniforms.primCount * 2u - 1u);
}
`;

// Sparse refit uses a compact list produced by broadphase AABB containment.
// It first marks complete dirty subtrees, then propagates readiness upward.
// Counting only marked immediate children is what makes a parent with one
// dirty and one clean child ready after a single arrival.
export const lbvhSparsePrepareShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> dirtyLeaves: array<u32>;
@group(0) @binding(2) var<storage, read_write> indirectDispatch: array<u32>;
@group(0) @binding(3) var<storage, read_write> refitState: array<atomic<u32>>;
@group(0) @binding(4) var<storage, read_write> sparseGateDispatch: array<u32>;

const WORKGROUP_SIZE: u32 = ${WORKGROUP_SIZE}u;
const FLAG_FORCE_FULL: u32 = 1u;

@compute @workgroup_size(1)
fn prepareSparseRefit() {
  for (var idx = 0u; idx < 12u; idx++) {
    atomicStore(&refitState[idx], 0u);
  }
  let dirtyCount = dirtyLeaves[0u];
  let inputFault = dirtyLeaves[1u] != 0u
    || dirtyLeaves[4u] != 0u
    || dirtyCount > uniforms.dirtyCapacity;
  let forced = (uniforms.flags & FLAG_FORCE_FULL) != 0u;
  // Sparse ancestor marking is O(dirty * depth).  Once a meaningful fraction
  // of the tree is dirty, a directly gated O(N) full refit is cheaper.  This
  // decision stays on the GPU, where the current dirty count is available.
  let denseLimit = min(
    uniforms.primCount,
    max(64u, min(4096u, max(1u, uniforms.primCount / 8u))),
  );
  let dense = dirtyCount > denseLimit;
  atomicStore(&refitState[0u], select(0u, 1u, inputFault));
  atomicStore(&refitState[5u], select(0u, 1u, forced));
  atomicStore(&refitState[7u], select(0u, 1u, dense));
  let sparseCount = select(
    min(dirtyCount, uniforms.dirtyCapacity),
    0u,
    inputFault || forced || dense,
  );
  indirectDispatch[0u] = (sparseCount + WORKGROUP_SIZE - 1u) / WORKGROUP_SIZE;
  indirectDispatch[1u] = 1u;
  indirectDispatch[2u] = 1u;
  sparseGateDispatch[0u] = select(0u, 1u, sparseCount > 0u);
  sparseGateDispatch[1u] = 1u;
  sparseGateDispatch[2u] = 1u;
}
`;

export const lbvhSparseLeavesShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> positions: array<f32>;
@group(0) @binding(2) var<storage, read> indices: array<u32>;
@group(0) @binding(3) var<storage, read_write> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(4) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(5) var<storage, read_write> visitCount: array<atomic<u32>>;
@group(0) @binding(6) var<storage, read_write> refitMarks: array<atomic<u32>>;
@group(0) @binding(7) var<storage, read> dirtyLeaves: array<u32>;
@group(0) @binding(8) var<storage, read_write> refitState: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;
const DIRTY_LIST_OFFSET: u32 = 8u;

fn loadPosition(vertexIdx: u32) -> vec3f {
  let base = vertexIdx * uniforms.positionStride;
  return vec3f(positions[base], positions[base + 1u], positions[base + 2u]);
}

fn finite3(v: vec3f) -> bool {
  return all(v == v) && all(abs(v) <= vec3f(1e20));
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn refitSparseLeaves(@builtin(global_invocation_id) globalId: vec3u) {
  let listIdx = globalId.x;
  let dirtyCount = min(dirtyLeaves[0u], uniforms.dirtyCapacity);
  if (listIdx >= dirtyCount) { return; }
  let leaf = dirtyLeaves[DIRTY_LIST_OFFSET + listIdx];
  // Appended-tail bodies are exact-scanned by the candidate shader and are
  // intentionally absent from this completed-tree prefix.
  if (leaf >= uniforms.primCount) { return; }
  atomicAdd(&refitState[1u], 1u);

  let indexBase = leaf * 3u;
  let v0 = loadPosition(indices[indexBase]);
  let v1 = loadPosition(indices[indexBase + 1u]);
  let v2 = loadPosition(indices[indexBase + 2u]);
  let boundsMin = min(min(v0, v1), v2);
  let boundsMax = max(max(v0, v1), v2);
  if (!finite3(boundsMin) || !finite3(boundsMax) || any(boundsMax < boundsMin)) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  var leafNode = bvh2Nodes[leaf];
  leafNode.boundsMin = boundsMin;
  leafNode.boundsMax = boundsMax;
  leafNode.leftChild = INVALID_IDX;
  leafNode.rightChild = leaf;
  bvh2Nodes[leaf] = leafNode;

  let maxNodes = uniforms.primCount * 2u;
  var node = leaf;
  var steps = 0u;
  loop {
    if (node >= maxNodes || steps >= maxNodes) {
      atomicStore(&refitState[0u], 1u);
      break;
    }
    let previousEpoch = atomicExchange(&refitMarks[node], uniforms.epoch);
    if (previousEpoch != uniforms.epoch) {
      atomicStore(&visitCount[node], 0u);
      if (node >= uniforms.primCount) {
        atomicAdd(&refitState[4u], 1u);
      }
    }
    let parent = parentIdx[node];
    if (parent == INVALID_IDX) {
      let validRoot = select(node == 0u, node == uniforms.primCount, uniforms.primCount > 1u);
      if (!validRoot) {
        atomicStore(&refitState[0u], 1u);
      }
      break;
    }
    if (parent == node || parent >= maxNodes || parent < uniforms.primCount) {
      atomicStore(&refitState[0u], 1u);
      break;
    }
    node = parent;
    steps += 1u;
  }
}
`;

export const lbvhSparseSeedShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(2) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(3) var<storage, read_write> visitCount: array<atomic<u32>>;
@group(0) @binding(4) var<storage, read> refitMarks: array<u32>;
@group(0) @binding(5) var<storage, read> dirtyLeaves: array<u32>;
@group(0) @binding(6) var<storage, read_write> activeListOut: array<u32>;
@group(0) @binding(7) var<storage, read_write> activeCountOut: atomic<u32>;
@group(0) @binding(8) var<storage, read_write> refitState: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;
const DIRTY_LIST_OFFSET: u32 = 8u;

fn notifyParent(parent: u32) {
  let maxNodes = uniforms.primCount * 2u;
  if (parent < uniforms.primCount || parent >= maxNodes) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let node = bvh2Nodes[parent];
  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (c0 == INVALID_IDX || c1 == INVALID_IDX || c0 >= maxNodes || c1 >= maxNodes) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let required = select(0u, 1u, refitMarks[c0] == uniforms.epoch)
    + select(0u, 1u, refitMarks[c1] == uniforms.epoch);
  if (required == 0u) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let arrived = atomicAdd(&visitCount[parent], 1u) + 1u;
  if (arrived == required) {
    let writeIdx = atomicAdd(&activeCountOut, 1u);
    if (writeIdx < uniforms.primCount) {
      activeListOut[writeIdx] = parent;
    } else {
      atomicStore(&refitState[0u], 1u);
    }
  } else if (arrived > required) {
    atomicStore(&refitState[0u], 1u);
  }
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn seedSparseRefit(@builtin(global_invocation_id) globalId: vec3u) {
  let listIdx = globalId.x;
  let dirtyCount = min(dirtyLeaves[0u], uniforms.dirtyCapacity);
  if (listIdx >= dirtyCount) { return; }
  let leaf = dirtyLeaves[DIRTY_LIST_OFFSET + listIdx];
  if (leaf >= uniforms.primCount) { return; }
  let parent = parentIdx[leaf];
  if (parent == INVALID_IDX) {
    if (uniforms.primCount > 1u) {
      atomicStore(&refitState[0u], 1u);
    }
    return;
  }
  notifyParent(parent);
}
`;

export const lbvhSparseWaveShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read_write> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(2) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(3) var<storage, read_write> visitCount: array<atomic<u32>>;
@group(0) @binding(4) var<storage, read> refitMarks: array<u32>;
@group(0) @binding(5) var<storage, read> activeListIn: array<u32>;
@group(0) @binding(6) var<storage, read_write> activeListOut: array<u32>;
@group(0) @binding(7) var<storage, read_write> activeCountIn: atomic<u32>;
@group(0) @binding(8) var<storage, read_write> activeCountOut: atomic<u32>;
@group(0) @binding(9) var<storage, read_write> refitState: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

fn finite3(v: vec3f) -> bool {
  return all(v == v) && all(abs(v) <= vec3f(1e20));
}

fn notifyParent(parent: u32) {
  let maxNodes = uniforms.primCount * 2u;
  if (parent < uniforms.primCount || parent >= maxNodes) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let node = bvh2Nodes[parent];
  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (c0 == INVALID_IDX || c1 == INVALID_IDX || c0 >= maxNodes || c1 >= maxNodes) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let required = select(0u, 1u, refitMarks[c0] == uniforms.epoch)
    + select(0u, 1u, refitMarks[c1] == uniforms.epoch);
  if (required == 0u) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let arrived = atomicAdd(&visitCount[parent], 1u) + 1u;
  if (arrived == required) {
    let writeIdx = atomicAdd(&activeCountOut, 1u);
    if (writeIdx < uniforms.primCount) {
      activeListOut[writeIdx] = parent;
    } else {
      atomicStore(&refitState[0u], 1u);
    }
  } else if (arrived > required) {
    atomicStore(&refitState[0u], 1u);
  }
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn refitSparseWave(@builtin(global_invocation_id) globalId: vec3u) {
  let rawCount = atomicLoad(&activeCountIn);
  if (rawCount > uniforms.primCount) {
    atomicStore(&refitState[0u], 1u);
  }
  let activeCount = min(rawCount, uniforms.primCount);
  let idx = globalId.x;
  if (idx >= activeCount) { return; }
  let maxNodes = uniforms.primCount * 2u;
  let nodeIdx = activeListIn[idx];
  if (
    nodeIdx < uniforms.primCount || nodeIdx >= maxNodes
    || refitMarks[nodeIdx] != uniforms.epoch
  ) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let node = bvh2Nodes[nodeIdx];
  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (
    c0 == INVALID_IDX || c1 == INVALID_IDX || c0 >= maxNodes || c1 >= maxNodes
    || c0 == nodeIdx || c1 == nodeIdx
  ) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  let mergedMin = min(bvh2Nodes[c0].boundsMin, bvh2Nodes[c1].boundsMin);
  let mergedMax = max(bvh2Nodes[c0].boundsMax, bvh2Nodes[c1].boundsMax);
  if (!finite3(mergedMin) || !finite3(mergedMax) || any(mergedMax < mergedMin)) {
    atomicStore(&refitState[0u], 1u);
    return;
  }
  bvh2Nodes[nodeIdx].boundsMin = mergedMin;
  bvh2Nodes[nodeIdx].boundsMax = mergedMax;

  let parent = parentIdx[nodeIdx];
  if (parent == INVALID_IDX) {
    if (nodeIdx == uniforms.primCount) {
      atomicStore(&refitState[2u], 1u);
    } else {
      atomicStore(&refitState[0u], 1u);
    }
    return;
  }
  notifyParent(parent);
}
`;

export const lbvhSparseValidateShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};
@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read_write> refitState: array<atomic<u32>>;

@compute @workgroup_size(1)
fn validateSparseRefit() {
  if (
    uniforms.primCount > 1u
    && atomicLoad(&refitState[1u]) > 0u
    && atomicLoad(&refitState[2u]) == 0u
  ) {
    atomicStore(&refitState[0u], 1u);
  }
}
`;

export const lbvhFallbackPrepareShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};
@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read_write> refitState: array<atomic<u32>>;
@group(0) @binding(2) var<storage, read_write> indirectDispatch: array<u32>;
@group(0) @binding(3) var<storage, read_write> activeCount0: atomic<u32>;
@group(0) @binding(4) var<storage, read_write> activeCount1: atomic<u32>;
@group(0) @binding(5) var<storage, read_write> fallbackGateDispatch: array<u32>;

const WORKGROUP_SIZE: u32 = ${WORKGROUP_SIZE}u;

@compute @workgroup_size(1)
fn prepareFullFallback() {
  let sparseFault = atomicLoad(&refitState[0u]) != 0u;
  let fallback = sparseFault
    || atomicLoad(&refitState[5u]) != 0u
    || atomicLoad(&refitState[7u]) != 0u;
  atomicStore(&refitState[3u], select(0u, 1u, fallback));
  // Preserve why the sparse path was abandoned, then give the full refit and
  // its validator a fresh fault channel.  Overflow/dense policy is nonfatal
  // whenever the full fallback validates successfully.
  atomicStore(&refitState[10u], select(0u, 1u, sparseFault));
  atomicStore(&refitState[0u], 0u);
  atomicStore(&refitState[8u], 0u);
  atomicStore(&activeCount0, 0u);
  atomicStore(&activeCount1, 0u);
  let count = select(0u, uniforms.primCount, fallback);
  indirectDispatch[0u] = (count + WORKGROUP_SIZE - 1u) / WORKGROUP_SIZE;
  indirectDispatch[1u] = 1u;
  indirectDispatch[2u] = 1u;
  fallbackGateDispatch[0u] = select(0u, 1u, fallback);
  fallbackGateDispatch[1u] = 1u;
  fallbackGateDispatch[2u] = 1u;
}
`;

export const lbvhFallbackResetShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};
@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read_write> visitCount: array<atomic<u32>>;

@compute @workgroup_size(${WORKGROUP_SIZE})
fn resetFullFallback(@builtin(global_invocation_id) globalId: vec3u) {
  let idx = globalId.x;
  if (idx >= uniforms.primCount) { return; }
  atomicStore(&visitCount[idx], 0u);
  atomicStore(&visitCount[idx + uniforms.primCount], 0u);
}
`;

export const lbvhFallbackValidatePrepareShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};
@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> refitState: array<u32>;
@group(0) @binding(2) var<storage, read_write> indirectDispatch: array<u32>;

const WORKGROUP_SIZE: u32 = ${WORKGROUP_SIZE}u;

@compute @workgroup_size(1)
fn prepareFullValidation() {
  let nodeCount = select(0u, uniforms.primCount * 2u - 1u, uniforms.primCount > 0u);
  let count = select(0u, nodeCount, refitState[3u] != 0u);
  indirectDispatch[0u] = (count + WORKGROUP_SIZE - 1u) / WORKGROUP_SIZE;
  indirectDispatch[1u] = 1u;
  indirectDispatch[2u] = 1u;
}
`;

// Runs only after a conditional full fallback.  Parent/child reciprocity plus
// exactly one expected root proves that all nodes form one rooted tree; strict
// child containment then proves that the root encloses every live leaf.
export const lbvhFallbackValidateShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> bvh2Nodes: array<BVH2Node>;
@group(0) @binding(2) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(3) var<storage, read_write> refitState: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;

fn finite3(v: vec3f) -> bool {
  return all(v == v) && all(abs(v) <= vec3f(1e20));
}

fn fail() {
  atomicStore(&refitState[8u], 1u);
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn validateFullFallback(@builtin(global_invocation_id) globalId: vec3u) {
  let nodeIdx = globalId.x;
  let nodeCount = uniforms.primCount * 2u - 1u;
  if (uniforms.primCount == 0u || nodeIdx >= nodeCount) { return; }

  let expectedRoot = select(0u, uniforms.primCount, uniforms.primCount > 1u);
  let parent = parentIdx[nodeIdx];
  if (nodeIdx == expectedRoot) {
    if (parent != INVALID_IDX) { fail(); }
  } else {
    if (parent < uniforms.primCount || parent >= nodeCount) {
      fail();
      return;
    }
    let parentNode = bvh2Nodes[parent];
    if (parentNode.leftChild != nodeIdx && parentNode.rightChild != nodeIdx) {
      fail();
    }
  }

  // A dense-motion fallback changes bounds only; topology is immutable and
  // was already deep-validated when published. Repeating an O(N * depth)
  // parent-chain walk on every dense 50k/64k frame was substantially slower
  // than the refit itself. Retain that proof for first publication and for a
  // genuine sparse/input fault, while ordinary dense fallbacks keep the O(N)
  // reciprocity, finite-bounds, containment, and exact-merge checks below.
  let needsDeepTopologyProof = (uniforms.flags & 1u) != 0u
    || atomicLoad(&refitState[10u]) != 0u;
  if (needsDeepTopologyProof) {
    var ancestor = nodeIdx;
    var reachedExpectedRoot = false;
    for (var hop = 0u; hop < nodeCount; hop++) {
      if (ancestor == expectedRoot) {
        reachedExpectedRoot = true;
        break;
      }
      let ancestorParent = parentIdx[ancestor];
      if (
        ancestorParent < uniforms.primCount || ancestorParent >= nodeCount
        || ancestorParent == ancestor
      ) {
        break;
      }
      ancestor = ancestorParent;
    }
    if (!reachedExpectedRoot) {
      fail();
      return;
    }
  }

  let node = bvh2Nodes[nodeIdx];
  if (!finite3(node.boundsMin) || !finite3(node.boundsMax) || any(node.boundsMax < node.boundsMin)) {
    fail();
    return;
  }

  if (nodeIdx < uniforms.primCount) {
    if (node.leftChild != INVALID_IDX || node.rightChild != nodeIdx) { fail(); }
    return;
  }

  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (
    c0 == INVALID_IDX || c1 == INVALID_IDX || c0 == c1
    || c0 >= nodeCount || c1 >= nodeCount || c0 == nodeIdx || c1 == nodeIdx
  ) {
    fail();
    return;
  }
  if (parentIdx[c0] != nodeIdx || parentIdx[c1] != nodeIdx) {
    fail();
    return;
  }

  let child0 = bvh2Nodes[c0];
  let child1 = bvh2Nodes[c1];
  if (
    !finite3(child0.boundsMin) || !finite3(child0.boundsMax)
    || !finite3(child1.boundsMin) || !finite3(child1.boundsMax)
    || any(node.boundsMin > child0.boundsMin) || any(node.boundsMax < child0.boundsMax)
    || any(node.boundsMin > child1.boundsMin) || any(node.boundsMax < child1.boundsMax)
  ) {
    fail();
    return;
  }

  // A relative merge check catches stale over-large ancestors without making
  // correctness depend on exact floating-point equality.
  let expectedMin = min(child0.boundsMin, child1.boundsMin);
  let expectedMax = max(child0.boundsMax, child1.boundsMax);
  let scale = max(vec3f(1.0), max(abs(expectedMin), abs(expectedMax)));
  let epsilon = scale * 2e-6;
  if (any(abs(node.boundsMin - expectedMin) > epsilon) || any(abs(node.boundsMax - expectedMax) > epsilon)) {
    fail();
  }
}
`;

export const lbvhSparseFinalizeShader = /* wgsl */`
struct SparseUniforms {
  primCount: u32,
  positionStride: u32,
  epoch: u32,
  dirtyCapacity: u32,
  flags: u32,
  debugOffset: u32,
  _pad0: u32,
  _pad1: u32,
};
@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> dirtyLeaves: array<u32>;
@group(0) @binding(2) var<storage, read_write> refitState: array<atomic<u32>>;
@group(0) @binding(3) var<storage, read_write> debugCounters: array<atomic<u32>>;

@compute @workgroup_size(1)
fn finalizeSparseRefit() {
  let base = uniforms.debugOffset;
  let dirtyCount = min(dirtyLeaves[0u], uniforms.dirtyCapacity);
  let fallback = atomicLoad(&refitState[3u]) != 0u;
  let forced = atomicLoad(&refitState[5u]) != 0u;
  let dense = atomicLoad(&refitState[7u]) != 0u;
  let sparseFault = atomicLoad(&refitState[10u]) != 0u;
  // Dirty-list overflow, dense policy, and explicit first-frame fallback are
  // recovered by the same-frame full refit.  Exact O(N^2) is reserved for a
  // topology/bounds failure detected *after* that full fallback.
  let newFatal = fallback && atomicLoad(&refitState[8u]) != 0u;
  // Keep a bad topology fail-open until the CPU publishes a replacement and
  // explicitly clears this per-topology latch. A later zero-dirty frame must
  // never silently resume traversal of the same invalid tree.
  let fatal = newFatal || atomicLoad(&debugCounters[base + 6u]) != 0u;
  atomicStore(&refitState[6u], select(0u, 1u, fatal));
  atomicStore(&debugCounters[base + 0u], dirtyCount);
  atomicStore(&debugCounters[base + 1u], atomicLoad(&refitState[4u]));
  atomicStore(&debugCounters[base + 2u], dirtyLeaves[2u]);
  atomicStore(&debugCounters[base + 3u], dirtyLeaves[3u]);
  // base+16 is the broadphase scene-sticky fatal word. Keep the certified
  // sleeping-AABB count in the next dedicated telemetry slot.
  atomicStore(&debugCounters[base + 17u], dirtyLeaves[7u]);
  if (!fallback) { atomicAdd(&debugCounters[base + 4u], 1u); }
  if (fallback) { atomicAdd(&debugCounters[base + 5u], 1u); }
  atomicStore(&debugCounters[base + 6u], select(0u, 1u, fatal));
  if (newFatal) { atomicAdd(&debugCounters[base + 7u], 1u); }
  // base+8..11 are reserved for the existing quality counters.
  if (forced) { atomicAdd(&debugCounters[base + 12u], 1u); }
  if (dense) { atomicAdd(&debugCounters[base + 13u], 1u); }
  if (sparseFault) { atomicAdd(&debugCounters[base + 14u], 1u); }
  if (newFatal) { atomicAdd(&debugCounters[base + 15u], 1u); }
}
`;

// A scale-invariant local SAH proxy. Each internal node contributes
// area(parent)/(area(left)+area(right)); spatially incoherent siblings grow
// this ratio while uniform world scaling leaves it unchanged. The fixed-point
// sum is bounded for the engine's 16-bit body index space.
export const lbvhQualityProbeShader = /* wgsl */`
struct Uniforms {
  primCount: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read> bvh2Nodes: array<BVH2Node>;
// 0 current fixed-point sum, 1 rebuild baseline, 2 ratio * 1e6,
// 3 current sample fault, 4 completed samples, 5 cumulative sample faults.
@group(0) @binding(2) var<storage, read_write> qualityState: array<atomic<u32>>;

const INVALID_IDX: u32 = 0xFFFFFFFFu;
const QUALITY_SCALE: f32 = 1024.0;
var<workgroup> partialQuality: array<u32, ${WORKGROUP_SIZE}>;

fn finite3(v: vec3f) -> bool {
  return all(v == v) && all(abs(v) <= vec3f(1e20));
}

fn finite1(v: f32) -> bool {
  return v == v && abs(v) < 3.4e38;
}

fn surfaceArea(boundsMin: vec3f, boundsMax: vec3f) -> f32 {
  let extent = max(boundsMax - boundsMin, vec3f(0.0));
  return 2.0 * (extent.x * extent.y + extent.y * extent.z + extent.z * extent.x);
}

fn nodeQualityContribution(internalId: u32) -> u32 {
  let maxNodes = uniforms.primCount * 2u;
  let nodeIdx = uniforms.primCount + internalId;
  let node = bvh2Nodes[nodeIdx];
  let c0 = node.leftChild;
  let c1 = node.rightChild;
  if (
    c0 == INVALID_IDX || c1 == INVALID_IDX
    || c0 >= maxNodes || c1 >= maxNodes
    || c0 == nodeIdx || c1 == nodeIdx
  ) {
    atomicStore(&qualityState[3u], 1u);
    return 0u;
  }

  let child0 = bvh2Nodes[c0];
  let child1 = bvh2Nodes[c1];
  if (
    !finite3(node.boundsMin) || !finite3(node.boundsMax)
    || !finite3(child0.boundsMin) || !finite3(child0.boundsMax)
    || !finite3(child1.boundsMin) || !finite3(child1.boundsMax)
    || any(node.boundsMax < node.boundsMin)
    || any(child0.boundsMax < child0.boundsMin)
    || any(child1.boundsMax < child1.boundsMin)
  ) {
    atomicStore(&qualityState[3u], 1u);
    return 0u;
  }

  if (
    any(node.boundsMin > child0.boundsMin)
    || any(node.boundsMax < child0.boundsMax)
    || any(node.boundsMin > child1.boundsMin)
    || any(node.boundsMax < child1.boundsMax)
  ) {
    atomicStore(&qualityState[3u], 1u);
    return 0u;
  }

  let parentArea = surfaceArea(node.boundsMin, node.boundsMax);
  let childArea = surfaceArea(child0.boundsMin, child0.boundsMax)
    + surfaceArea(child1.boundsMin, child1.boundsMax);
  if (!finite1(parentArea) || !finite1(childArea)) {
    atomicStore(&qualityState[3u], 1u);
    return 0u;
  }
  let localCost = clamp(parentArea / max(childArea, 1e-12), 0.0, 16.0);
  if (!finite1(localCost)) {
    atomicStore(&qualityState[3u], 1u);
    return 0u;
  }
  return u32(localCost * QUALITY_SCALE + 0.5);
}

@compute @workgroup_size(${WORKGROUP_SIZE})
fn probeQuality(
  @builtin(global_invocation_id) globalId: vec3u,
  @builtin(local_invocation_id) localId: vec3u,
) {
  let internalId = globalId.x;
  var contribution = 0u;
  if (uniforms.primCount > 1u && internalId < uniforms.primCount - 1u) {
    contribution = nodeQualityContribution(internalId);
  }
  partialQuality[localId.x] = contribution;
  workgroupBarrier();

  // Reduce 256 contending global atomics to one per workgroup. The sum is
  // unchanged, so the quality ratio/rebuild policy remains deterministic.
  var stride = ${WORKGROUP_SIZE / 2}u;
  loop {
    if (localId.x < stride) {
      partialQuality[localId.x] += partialQuality[localId.x + stride];
    }
    workgroupBarrier();
    if (stride == 1u) { break; }
    stride = stride / 2u;
  }
  if (localId.x == 0u && partialQuality[0u] > 0u) {
    atomicAdd(&qualityState[0u], partialQuality[0u]);
  }
}
`;

export const lbvhQualityFinalizeShader = /* wgsl */`
@group(0) @binding(0) var<storage, read_write> qualityState: array<atomic<u32>>;

const RATIO_SCALE: f32 = 1000000.0;
const FAULT_RATIO: u32 = 4000000u;

@compute @workgroup_size(1)
fn finalizeBaseline() {
  let current = max(atomicLoad(&qualityState[0u]), 1u);
  atomicStore(&qualityState[1u], current);
  atomicStore(
    &qualityState[2u],
    select(1000000u, FAULT_RATIO, atomicLoad(&qualityState[3u]) != 0u),
  );
}

@compute @workgroup_size(1)
fn finalizeSample() {
  let current = max(atomicLoad(&qualityState[0u]), 1u);
  let baseline = max(atomicLoad(&qualityState[1u]), 1u);
  let fault = atomicLoad(&qualityState[3u]) != 0u;
  let ratio = u32(clamp(f32(current) / f32(baseline) * RATIO_SCALE, 0.0, 4000000.0));
  atomicStore(&qualityState[2u], select(ratio, FAULT_RATIO, fault));
  atomicAdd(&qualityState[4u], 1u);
  if (fault) {
    atomicAdd(&qualityState[5u], 1u);
  }
}
`;
